import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Typography } from '@mui/material';
import { memo } from 'react';

import { shouldShowUOM } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { PropertyCatalogBadge } from 'shared/ui/PropertyCatalogBadge';
import { UnitOfMeasureDisplay } from 'shared/ui/UnitOfMeasureDisplay/UnitOfMeasureDisplay';
import { decodeHtmlEntities } from 'shared/utils/textFormatters/textFormatters';

const TITLE_TEXT_CLASS = 'product-row-title-text';
const TITLE_ARROW_CLASS = 'product-row-title-arrow';

export interface ProductRowTitleBlockProps {
    title: string;
    brand?: string;
    uom: string;
    qtyPerUom: number;
    isAddedToCatalog?: boolean;
    interactive: boolean;
    onClickTitle?: () => void;
    dimmed?: boolean;
    titleDataTestClass: string;
    brandUomDataTestClass: string;
}

export const ProductRowTitleBlock = memo(
    ({
        title,
        brand,
        uom,
        qtyPerUom,
        isAddedToCatalog = false,
        interactive,
        onClickTitle,
        dimmed = false,
        titleDataTestClass,
        brandUomDataTestClass,
    }: ProductRowTitleBlockProps) => {
        const showBrandUomRow = Boolean(brand) || shouldShowUOM(uom);

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                <Box
                    onClick={interactive ? onClickTitle : undefined}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        maxWidth: '100%',
                        width: '100%',
                        cursor: interactive ? 'pointer' : 'default',
                        opacity: dimmed ? 0.35 : 1,
                        ...(interactive && {
                            [`&:hover .${TITLE_TEXT_CLASS}`]: {
                                color: '#3F6EB7',
                            },
                            [`&:hover .${TITLE_ARROW_CLASS}`]: {
                                opacity: 1,
                            },
                        }),
                    }}
                >
                    {isAddedToCatalog && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                marginRight: '8px',
                                marginTop: '2px',
                            }}
                        >
                            <PropertyCatalogBadge />
                        </Box>
                    )}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-block',
                                maxWidth: '100%',
                                width: 'fit-content',
                                verticalAlign: 'top',
                                overflow: 'hidden',
                            }}
                        >
                            <ChevronRightIcon
                                className={TITLE_ARROW_CLASS}
                                sx={{
                                    float: 'right',
                                    display: 'block',
                                    fontSize: '24px',
                                    color: '#3F6EB7',
                                    opacity: 0,
                                    transition: 'opacity 0.15s',
                                    marginLeft: '0',
                                    marginTop: '-1px',
                                }}
                            />
                            <Typography
                                className={TITLE_TEXT_CLASS}
                                typography={'openSans.subtitle1Medium'}
                                data-testclass={titleDataTestClass}
                                component="span"
                                sx={{
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    letterSpacing: '0',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: '2',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    transition: 'color 0.15s',
                                }}
                            >
                                {decodeHtmlEntities(title)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {showBrandUomRow && (
                    <Box
                        data-testclass={brandUomDataTestClass}
                        sx={{
                            display: 'flex',
                            gap: '8px',
                            marginTop: '1px',
                            opacity: dimmed ? 0.35 : 1,
                        }}
                    >
                        {brand && (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: '13px',
                                    letterSpacing: '0',
                                    lineHeight: '16px',
                                }}
                            >
                                {decodeHtmlEntities(brand)}
                            </Typography>
                        )}
                        {shouldShowUOM(uom) && (
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '13px',
                                    letterSpacing: '0',
                                    lineHeight: '16px',
                                }}
                            >
                                <UnitOfMeasureDisplay
                                    unitCode={uom}
                                    quantity={qtyPerUom}
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    },
);

ProductRowTitleBlock.displayName = 'ProductRowTitleBlock';
